import React, {SVGProps, useRef, useState} from "react";
import {v4 as uuidv4} from "uuid";
import {useResizeObserver, useStyle} from "../../hooks";
import {RelativePosition} from "../nodes/FancyExecutableNode";
import store, {useReduxState, useSelectedNodes} from "../../store";
import {_scale} from "../nodes";
import {DraggableCore, DraggableEventHandler} from "react-draggable";
import {commands} from "../../common/session/CommandHandler";
import {multi, updateNode} from "../../store/nodes";
import {motion} from "framer-motion";
import {hasRectangle, RectangleSelection, useZoom} from "./ZoomTranslate";

const DashedSVGBackground = (props: SVGProps<SVGSVGElement>) => {
    let id = uuidv4();
    let key = "dashed-bg-" + id;

    let w = 24;
    let style = useStyle().palette.highlight.ghost;
    const ref = useRef<SVGSVGElement>(null);
    const rect = useResizeObserver(ref);
    const w2 = w * 2;
    const rRect = {
        x: Math.ceil(rect.x / w2) * w2,
        y: Math.ceil(rect.y / w2) * w2,
        width: Math.ceil(rect.width / w2) * w2,
        height: Math.ceil(rect.height / w2) * w2
    }
    console.log(rRect)
    console.log(rect)

    return <svg
        ref={ref}
        {...props}
        style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: "100%",
            height: "100%",
            ...props.style
        }}>
        <defs>
            <pattern id={key} viewBox={"0,0,100,100"} width={w} height={w} patternUnits="userSpaceOnUse">
                {/*<rect x={0} y={0} width="100" height="100" fill={style} fillOpacity={.333}/>*/}
                <line
                    x1={-50}
                    y1={50}
                    x2={50}
                    y2={-50}
                    stroke={style}
                    strokeWidth={35}
                />
                <line
                    x1={0}
                    y1={100}
                    x2={100}
                    y2={0}
                    stroke={style}
                    strokeWidth={35}
                />
                <line
                    x1={50}
                    y1={150}
                    x2={150}
                    y2={50}
                    stroke={style}
                    strokeWidth={35}
                />
            </pattern>
        </defs>
        <svg x={-(rRect.width - rect.width) / 2} y={-(rRect.height - rect.height) / 2} width="calc(100% + 24px)"
             height="calc(100% + 24px)">
            <rect width="100%" height="100%" fill={`url(#${key})`} fillOpacity={1}/>
        </svg>
        {/*<svg width={120} height={120} style={{
                fillRule: 'evenodd',
                clipRule: 'evenodd'
            }}>
            <TarsyHelmet/>
        </svg>*/}
    </svg>
}

const RectangleSelector = ({initialBox}: { initialBox: RectangleSelection | null }) => {
    const [relMousePos, setRelMousePos] = useState<RelativePosition | null>(null);
    const [selectionBox, setSelectionBox] = useReduxState<RectangleSelection | undefined>('UPDATESELECTIONBOX', state => state.nodes.selectionBox);
    const [selected] = useSelectedNodes();
    const [init, setInit] = useState<boolean>(true);
    const style = useStyle();
    const dragRef = useRef<HTMLDivElement>(null)

    const [hover, setHover] = useState<boolean>(false);
    const [cursor, setCursor] = useState<"grab" | "grabbing">("grab");

    const zoom = useZoom();
    const screen = (x: number) => x * _scale * zoom;
    const world = (x: number) => x / _scale / zoom;

    const handleMouseEnter = () => setHover(true);
    const handleMouseLeave = () => setHover(false);

    const selectionFn = (x: number) => Math.pow(Math.sin((x - .5) * Math.PI) / 2 + .5, .25);

    const handleStart: DraggableEventHandler = (e, data) => {
        if (!selectionBox) return;

        setRelMousePos({
            left: data.x - screen(selectionBox.x1),
            top: data.y - screen(selectionBox.y1),
            right: screen(selectionBox.x2) - data.x,
            bottom: screen(selectionBox.y2) - data.y
        });

        setCursor("grabbing");
        setInit(false);

        commands.memoNodes();
    }

    const handleDrag: DraggableEventHandler = (e, data) => {
        if (!relMousePos || !selectionBox) return;

        let snappingActive = !!store.getState().workspaceSettings["snap"];

        let newX = world(data.x - relMousePos.left);
        let newY = world(data.y - relMousePos.top);

        let w = world(relMousePos.left + relMousePos.right);
        let h = world(relMousePos.top + relMousePos.bottom);
        let newBox = {
            x1: newX,
            y1: newY,
            x2: newX + w,
            y2: newY + h
        }

        if (snappingActive) {
            let sx1 = Math.round(newBox.x1 / 6) * 6;
            let sy1 = Math.round(newBox.y1 / 6) * 6;
            let sx2 = Math.round(newBox.x2 / 6) * 6;
            let sy2 = Math.round(newBox.y2 / 6) * 6;

            let asx1 = Math.abs(sx1 - newBox.x1);
            let asy1 = Math.abs(sy1 - newBox.y1);
            let asx2 = Math.abs(sx2 - newBox.x2);
            let asy2 = Math.abs(sy2 - newBox.y2);

            if (asx1 < asx2) {
                newBox.x1 = sx1;
                newBox.x2 = sx1 + w;
            } else {
                newBox.x2 = sx2;
                newBox.x1 = sx2 - w;
            }

            if (asy1 < asy2) {
                newBox.y1 = sy1;
                newBox.y2 = sy1 + h;
            } else {
                newBox.y2 = sy2;
                newBox.y1 = sy2 - h;
            }
        }

        let diff = {
            x1: newBox.x1 - selectionBox.x1,
            y1: newBox.y1 - selectionBox.y1,
            x2: newBox.x2 - selectionBox.x2,
            y2: newBox.y2 - selectionBox.y2
        }

        if (selected) {
            store.dispatch(
                multi(
                    selected.map(id => {
                        let node = store.getState().nodes.nodes[id];
                        return updateNode({
                            id,
                            shape: {
                                x: node.shape.x + diff.x1,
                                y: node.shape.y + diff.y1
                            }
                        });
                    })
                )
            );
        }

        setSelectionBox(newBox ?? null);
    }

    const handleStop: DraggableEventHandler = (e, data) => {
        setCursor("grab");
        setRelMousePos(null);
    }

    if (initialBox && selectionBox && hasRectangle(initialBox)) return <DraggableCore
        nodeRef={dragRef}
        onStart={handleStart}
        onDrag={handleDrag}
        onStop={handleStop}
    >
        <div ref={dragRef} style={{opacity: hover ? 1 : .5}}>
            {init ?
                <motion.div
                    onMouseOver={handleMouseEnter}
                    onMouseOut={handleMouseLeave}
                    //key={"selection rectangle " + selectionKey}
                    style={{
                        position: 'absolute',
                        //border: '2px solid #ff0000'
                        background: style.palette.highlight.ghost,
                        overflow: "hidden",
                        cursor
                    }}
                    transition={{ease: selectionFn}}
                    initial={{
                        left: Math.min(initialBox.x1, initialBox.x2) * _scale,
                        top: Math.min(initialBox.y1, initialBox.y2) * _scale,
                        width: (Math.max(initialBox.x1, initialBox.x2) - Math.min(initialBox.x1, initialBox.x2)) * _scale,
                        height: (Math.max(initialBox.y1, initialBox.y2) - Math.min(initialBox.y1, initialBox.y2)) * _scale,
                        borderRadius: 0,
                        opacity: 0
                    }}
                    animate={{
                        left: Math.min(selectionBox.x1, selectionBox.x2) * _scale,
                        top: Math.min(selectionBox.y1, selectionBox.y2) * _scale,
                        width: (Math.max(selectionBox.x1, selectionBox.x2) - Math.min(selectionBox.x1, selectionBox.x2)) * _scale,
                        height: (Math.max(selectionBox.y1, selectionBox.y2) - Math.min(selectionBox.y1, selectionBox.y2)) * _scale,
                        borderRadius: 16,
                        opacity: 1
                    }}>
                    <DashedSVGBackground style={{opacity: .333}}/>
                </motion.div> :
                <div
                    style={{
                        position: 'absolute',
                        //border: '2px solid #ff0000'
                        background: style.palette.highlight.ghost,
                        overflow: "hidden",
                        cursor,
                        left: Math.min(selectionBox.x1, selectionBox.x2) * _scale,
                        top: Math.min(selectionBox.y1, selectionBox.y2) * _scale,
                        width: (Math.max(selectionBox.x1, selectionBox.x2) - Math.min(selectionBox.x1, selectionBox.x2)) * _scale,
                        height: (Math.max(selectionBox.y1, selectionBox.y2) - Math.min(selectionBox.y1, selectionBox.y2)) * _scale,
                        borderRadius: 16,
                        opacity: 1
                    }}
                    onMouseOver={handleMouseEnter}
                    onMouseOut={handleMouseLeave}
                >
                    <DashedSVGBackground style={{opacity: .333}}/>
                </div>
            }
        </div>
    </DraggableCore>; else return null;
}

export default RectangleSelector;